import * as React from "react";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { FormControl, InputLabel, MenuItem, Select, Typography } from "@mui/material";

const PAYOUT_FREQUENCY_OPTIONS = ["MONTHLY", "WEEKLY", "DAILY"];
const PAY_WEEK_DAY_OPTIONS = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];
const LANDLORD_PAYMENT_MODE_OPTIONS = ["MOBILE_MONEY", "BANK", "CASH", "OTHER"];

const AddLandlordForm = ({ newLandlord, handleChange, validationErrors }) => {
  const payoutFrequency = newLandlord.payout_frequency || "MONTHLY";
  const paymentMode = newLandlord.landlord_payment_mode || "MOBILE_MONEY";

  return (
    <Box padding={2}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="subtitle2" color="text.secondary">
            Account details
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <TextField
            margin="dense"
            label="Business name"
            type="text"
            fullWidth
            variant="outlined"
            name="business_name"
            value={newLandlord.business_name}
            onChange={handleChange}
            error={Boolean(validationErrors.business_name)}
            helperText={validationErrors.business_name}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            margin="dense"
            label="First name"
            type="text"
            fullWidth
            variant="outlined"
            name="first_name"
            value={newLandlord.first_name}
            onChange={handleChange}
            error={Boolean(validationErrors.first_name)}
            helperText={validationErrors.first_name}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            margin="dense"
            label="Last name"
            type="text"
            fullWidth
            variant="outlined"
            name="last_name"
            value={newLandlord.last_name}
            onChange={handleChange}
            error={Boolean(validationErrors.last_name)}
            helperText={validationErrors.last_name}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            margin="dense"
            label="Email (optional)"
            type="email"
            fullWidth
            variant="outlined"
            name="email"
            value={newLandlord.email}
            onChange={handleChange}
            error={Boolean(validationErrors.email)}
            helperText={validationErrors.email}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            margin="dense"
            label="Contact (+256)"
            type="tel"
            fullWidth
            variant="outlined"
            name="contact"
            placeholder="+256712345678"
            value={newLandlord.contact}
            onChange={handleChange}
            error={Boolean(validationErrors.contact)}
            helperText={
              validationErrors.contact ||
              "Used as login username and phone number"
            }
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6" fontWeight="bold" sx={{ mt: 1 }}>
            Payout schedule
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            margin="dense"
            label="Payment percentage"
            type="number"
            fullWidth
            variant="outlined"
            name="pay_percentage"
            value={newLandlord.pay_percentage ?? ""}
            onChange={handleChange}
            error={Boolean(validationErrors.pay_percentage)}
            helperText={validationErrors.pay_percentage}
            inputProps={{ min: 1, max: 100, step: "0.01" }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth margin="dense" error={Boolean(validationErrors.payout_frequency)}>
            <InputLabel>Payout frequency</InputLabel>
            <Select
              label="Payout frequency"
              name="payout_frequency"
              value={payoutFrequency}
              onChange={handleChange}
            >
              {PAYOUT_FREQUENCY_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
            {validationErrors.payout_frequency && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                {validationErrors.payout_frequency}
              </Typography>
            )}
          </FormControl>
        </Grid>

        {payoutFrequency === "MONTHLY" && (
          <Grid item xs={12} sm={6}>
            <TextField
              margin="dense"
              label="Pay day (day of month)"
              type="text"
              fullWidth
              variant="outlined"
              name="pay_day"
              value={newLandlord.pay_day}
              onChange={handleChange}
              error={Boolean(validationErrors.pay_day)}
              helperText={validationErrors.pay_day || "Max 5 characters, e.g. 7"}
            />
          </Grid>
        )}

        {payoutFrequency === "WEEKLY" && (
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth margin="dense" error={Boolean(validationErrors.pay_week_day)}>
              <InputLabel>Pay week day</InputLabel>
              <Select
                label="Pay week day"
                name="pay_week_day"
                value={newLandlord.pay_week_day || ""}
                onChange={handleChange}
              >
                {PAY_WEEK_DAY_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
              {validationErrors.pay_week_day && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                  {validationErrors.pay_week_day}
                </Typography>
              )}
            </FormControl>
          </Grid>
        )}

        <Grid item xs={12}>
          <Typography variant="h6" fontWeight="bold" sx={{ mt: 1 }}>
            Payout destination
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Stored as the landlord payout profile for matching and disbursements.
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth margin="dense" error={Boolean(validationErrors.landlord_payment_mode)}>
            <InputLabel>Payment mode</InputLabel>
            <Select
              label="Payment mode"
              name="landlord_payment_mode"
              value={paymentMode}
              onChange={handleChange}
            >
              {LANDLORD_PAYMENT_MODE_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option.replace(/_/g, " ")}
                </MenuItem>
              ))}
            </Select>
            {validationErrors.landlord_payment_mode && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                {validationErrors.landlord_payment_mode}
              </Typography>
            )}
          </FormControl>
        </Grid>

        {paymentMode === "MOBILE_MONEY" && (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                label="Mobile money number"
                type="text"
                fullWidth
                variant="outlined"
                name="landlord_payment_account"
                value={newLandlord.landlord_payment_account}
                onChange={handleChange}
                error={Boolean(validationErrors.landlord_payment_account)}
                helperText={
                  validationErrors.landlord_payment_account ||
                  "Optional — defaults to contact (+256) if empty"
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                margin="dense"
                label="Name on mobile money wallet"
                type="text"
                fullWidth
                variant="outlined"
                name="landlord_mobile_registered_name"
                value={newLandlord.landlord_mobile_registered_name}
                onChange={handleChange}
                error={Boolean(validationErrors.landlord_mobile_registered_name)}
                helperText={validationErrors.landlord_mobile_registered_name}
              />
            </Grid>
          </>
        )}

        {paymentMode === "BANK" && (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                label="Bank name"
                type="text"
                fullWidth
                variant="outlined"
                name="landlord_bank_name"
                value={newLandlord.landlord_bank_name}
                onChange={handleChange}
                error={Boolean(validationErrors.landlord_bank_name)}
                helperText={validationErrors.landlord_bank_name}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                label="Account number"
                type="text"
                fullWidth
                variant="outlined"
                name="landlord_bank_account_number"
                value={newLandlord.landlord_bank_account_number}
                onChange={handleChange}
                error={Boolean(validationErrors.landlord_bank_account_number)}
                helperText={validationErrors.landlord_bank_account_number}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                label="Account name"
                type="text"
                fullWidth
                variant="outlined"
                name="landlord_bank_account_name"
                value={newLandlord.landlord_bank_account_name}
                onChange={handleChange}
                error={Boolean(validationErrors.landlord_bank_account_name)}
                helperText={validationErrors.landlord_bank_account_name}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                label="Branch"
                type="text"
                fullWidth
                variant="outlined"
                name="landlord_bank_branch"
                value={newLandlord.landlord_bank_branch}
                onChange={handleChange}
                error={Boolean(validationErrors.landlord_bank_branch)}
                helperText={validationErrors.landlord_bank_branch}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                label="Sort code (optional)"
                type="text"
                fullWidth
                variant="outlined"
                name="landlord_bank_sort_code"
                value={newLandlord.landlord_bank_sort_code}
                onChange={handleChange}
                error={Boolean(validationErrors.landlord_bank_sort_code)}
                helperText={validationErrors.landlord_bank_sort_code}
              />
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
};

export default AddLandlordForm;
